import Vue from 'vue'
import VueRouter from 'vue-router'
import { useLaunchSetupStore } from '@/stores/LaunchSetup'
import { useAuthStore } from '@/stores/auth'
import { useEncounterStore } from '@/stores/encounter'
import { featureFlags } from '@/config/featureFlags'

import Login from '@/components/pages/Login'

import Language from '@/components/pages/Language';
import Device from '@/components/pages/Device';
import Start from '@/components/pages/Start';
import Summary from '@/components/pages/Summary';
import Preference from '@/components/pages/Preference';
import Location from '@/components/pages/Location';
import AssessmentMode from '@/components/pages/AssessmentMode';
import GuidedMode from '@/features/guided-mode/GuidedModePage.vue';
import Encounters from '@/components/pages/Encounters';
import SelectionMode from '@/components/AssessmentMode/SelectionMode';
import UserInterface from '@/components/pages/UserInterface';
import Settings from '@/components/pages/Settings';
import VoiceMode from '@/components/AssessmentMode/VoiceMode';
import PastPatientVisits from '@/components/pages/PastPatientVisits';
import AssessmentEntryPage from "@/features/assessment/pages/AssessmentEntryPage.vue";
import TextModePage from "@/features/text-mode/pages/TextModePage.vue";
import OverallAssessmentPage from "@/features/overall-assessment/pages/OverallAssessmentPage.vue";
import { useTestModeStore } from '@/stores/testMode';
import { useLocationStore } from '@/stores/location';
import { registerRouteCapture } from '@/logging/currentRoute';
import LaunchSetup from '@/components/pages/LaunchSetup.vue';

Vue.use(VueRouter)

const assessmentModeMeta = {
    ready: true,
    show: false,
    requiresCompletedSetup: true,
    requiresEncounter: true,
}

export const routeRequiresCompletedSetup = (route) =>
    route.matched.some(({ meta }) => meta.requiresCompletedSetup)

export const routeRequiresEncounter = (route) =>
    route.matched.some(({ meta }) => meta.requiresEncounter)

export const setupRedirectForRoute = (route, setupStore) => {
    if (!routeRequiresCompletedSetup(route)) return null
    if (!setupStore.configurationReady) return { name: 'Setup' }
    return setupStore.isSetupComplete ? null : setupStore.entryRoute
}

const routes = [
    {
        path: '/encounters',
        component: Encounters,
        name: 'Encounters',
        meta: {
            position: 'center',
            show: false,
        }
    },
    {
        path: '/setup/:group?',
        component: LaunchSetup,
        name: 'Setup',
        meta: {
            position: 'center',
        }
    },
    {
        path: '/language',
        component: Language,
        name: 'Language',
        meta: {
            position: 'center'
        }
    },
    {
        path: '/location*',
        component: Location,
        name: 'Location',
        meta: {
            position: 'center'
        }
    },
    {
        path: '/device',
        component: Device,
        name: 'Device',
        meta: {
            show: false,
            position: 'center'
        }
    }, {
        path: '/settings',
        component: Settings,
        name: 'Settings',
        meta: {
            position: 'center'
        }
    }, {
        path: '/preference/user_interface',
        component: UserInterface,
        name: 'UserInterface',
        meta: {
            ready: false,
            position: 'center'
        }
    },
    {
        path: '/preference/:panels_name(summary)',
        component: Summary,
        name: 'Summary',
        meta: {
            collection: 'preference',
            ready: true,
            position: 'center'
        }
    },
    {
        path: '/preference/:panels_name',
        component: Preference,
        name: 'Preference',
        meta: {
            collection: 'preference',
            position: 'center'
        }
    },{
        path: '/assessment_entry',
        component: AssessmentEntryPage,
        name: 'Assessment Entry',
        meta: {
            requiresCompletedSetup: true,
            requiresEncounter: true,
        },
    },
    {
        path: '/text-mode',
        component: TextModePage,
        name: 'TextMode',
        meta: {
            ...assessmentModeMeta,
            title: 'Text Mode',
        },
    },
    {
        path: '/overall-assessment',
        component: OverallAssessmentPage,
        name: 'OverallAssessment',
        meta: {
            ...assessmentModeMeta,
            title: 'Overall Assessment',
            fullWidth: true,
        },
    },
    {
        path: '/assessment_mode',
        component: AssessmentMode,
        name: 'AssessmentMode',
        meta: {
            ...assessmentModeMeta,
            title: 'Assessment Mode',
        },
        children: [
            {
                path: '',
                component: SelectionMode,
                name: 'AssessmentModeSelection',
                meta: {
                    ...assessmentModeMeta,
                    title: 'Assessment Mode',
                },
            },
            {
                path: 'guided',
                component: GuidedMode,
                name: 'AssessmentModeGuided',
                meta: {
                    ...assessmentModeMeta,
                    title: 'Guided Mode',
                },
            },
            {
                path: 'voice',
                component: VoiceMode,
                name: 'AssessmentModeVoice',
                meta: {
                    ...assessmentModeMeta,
                    title: 'Voice Mode',
                },
            },
            {
                path: 'text',
                component: TextModePage,
                name: 'AssessmentModeText',
                meta: {
                    ...assessmentModeMeta,
                    title: 'Text Mode',
                },
            },
        ]
    },{
        path: '/start/',
        component: Start,
        name: 'Start',
        meta: {
            ready: true,
            show: false,
            requiresCompletedSetup: true,
        }
    }, {
        path: '/past-visits',
        component: PastPatientVisits,
        name: 'PastPatientVisits',
        meta: {
            panel_name: 'past-visits',
            show: false,
            requiresPpv: true,
        }
    }, {
        path: '/',
        alias: '*',
        component: Login,
        name: 'Login',
        meta: {
            ready: true,
            show: false,
            top: false,
            sw: false,
            position: 'center'
        }
    },
]

const router = new VueRouter({ routes })

registerRouteCapture(router)

router.beforeEach((to, from, next) => {
    const launchSetupStore = useLaunchSetupStore();
    const testModeStore = useTestModeStore();
    const locationStore = useLocationStore();
    const authStore = useAuthStore();
    const encounterStore = useEncounterStore();
    const setupRedirect = setupRedirectForRoute(to, launchSetupStore);

    if (!authStore.authenticated()) {
        if (to.name === 'Login') {
            next()
        } else {
            next({ name: 'Login' })
        }
    } else if (to.name === 'Login' && authStore.authenticated()) {
        next(launchSetupStore.entryRoute)
    } else if (testModeStore.get('disableRouterGuards')) {
        next()
    } else if (setupRedirect) {
        next(setupRedirect)
    } else if (routeRequiresEncounter(to) && encounterStore.uuid === null) {
        next({ name: 'Start' })
    } else if (to.meta.requiresPpv && !featureFlags.enablePPV) {
        next({ name: 'Start' })
    } else if (to.meta.collection === 'preference') {
        if (locationStore.locationsIncomplete) {
            next({ path: '/location' })
        } else {
            next()
        }
    }
    else {
        next()
    }
})

let panelTimer = null
let currentPanelName = null
let _trackFn = null
let _events = null
let beforeUnloadRegistered = false

function getPanelName(route) {
    return route.meta?.panel_name || route.params?.panels_name || route.name || route.path || 'unknown'
}

/**
 * Returns true if the route should be tracked (excludes anonymous/auth-only routes).
 * Centralizing this condition avoids inconsistency when adding new excluded routes.
 * @param {object} route - Vue Router route object
 * @returns {boolean}
 */
function isTrackableRoute(route) {
    return Boolean(route.name && route.name !== 'Login')
}

function getTrackFn() {
    if (!_trackFn) {
        try {
            const { track } = require('@/plugins/analytics')
            _trackFn = track || (() => {})
        } catch (e) {
            _trackFn = () => {}
        }
    }
    return _trackFn
}

function getEvents() {
    if (!_events) {
        try {
            _events = require('@/plugins/analytics/events').EVENTS
        } catch (e) {
            _events = {}
        }
    }
    return _events
}

/**
 * Tracks a panel exit event — used on route change and tab close.
 * @param {string} panelName - Name of the panel being exited
 * @param {string} routePath - Route path of the panel
 * @param {object} [extra={}] - Additional properties (e.g. exit_type)
 */
function trackPanelExit(panelName, routePath, extra = {}) {
    try {
        const events = getEvents()
        const durationSeconds = panelTimer ? panelTimer.getSeconds() : 0
        const track = getTrackFn()
        track(events.PANEL_EXIT, {
            panel_name: panelName,
            route_path: routePath,
            session_duration_seconds: durationSeconds,
            ...extra,
        })
    } catch (e) { }
}

/**
 * Tracks a panel view event — used on route change.
 * @param {string} panelName - Name of the panel being entered
 * @param {string} routePath - Route path of the panel
 * @param {string} source - Route name the user came from
 */
function trackPanelView(panelName, routePath, source) {
    const events = getEvents()
    const track = getTrackFn()
    track(events.PANEL_VIEW, {
        panel_name: panelName,
        route_path: routePath,
        source: source || 'direct',
    })
}

router.afterEach((to, from) => {
    if (to.fullPath === from.fullPath) return

    if (isTrackableRoute(from) && panelTimer) {
        trackPanelExit(getPanelName(from), from.path)
        panelTimer.stop()
        panelTimer = null
    }

    if (isTrackableRoute(to)) {
        const { createActiveSessionTimer } = require('@/plugins/analytics/helpers')
        panelTimer = createActiveSessionTimer()
        panelTimer.start()
        currentPanelName = getPanelName(to)
        trackPanelView(currentPanelName, to.path, from.name)
    }
})

if (typeof window !== 'undefined' && !beforeUnloadRegistered) {
    beforeUnloadRegistered = true
    window.addEventListener('beforeunload', () => {
        if (!panelTimer || !currentPanelName) return

        const mixpanelToken = (window?.thinkmd && window?.thinkmd?.mixpanel_token) || process.env.VUE_APP_MIXPANEL_TOKEN
        try {
            const { isAnalyticsEnabled, buildEventProperties } = require('@/plugins/analytics')
            if (!isAnalyticsEnabled || !isAnalyticsEnabled()) return
            const { sendBeaconEvent } = require('@/plugins/analytics/helpers')
            const durationSeconds = panelTimer.getSeconds()
            const eventName = getEvents().PANEL_EXIT || 'panel_exit'
            sendBeaconEvent(eventName, buildEventProperties(eventName, {
                panel_name: currentPanelName,
                session_duration_seconds: durationSeconds,
                exit_type: 'tab_close',
            }), mixpanelToken)
        } catch (e) { }
    })
}

export default router
